/**
 * Procedural sound presets built from oscillators, noise buffers and gain envelopes.
 * No samples needed: every SoundEvent has a synth fallback so a theme works without audio files.
 */
import type { SynthPreset } from '../types';

export interface SynthHandle {
  stop(): void;
}

/** Everything a preset creates, so stop() can tear it down. */
interface Voice {
  ctx: AudioContext;
  out: GainNode;
  volume: number;
  loop: boolean;
  sources: Set<AudioScheduledSourceNode>;
  timers: Set<ReturnType<typeof setTimeout>>;
  /** Called when a one-shot's last source has ended. */
  onDrained: () => void;
}

const noiseCache = new WeakMap<AudioContext, AudioBuffer>();

/** 2 seconds of white noise, cached per context. */
function noiseBuffer(ctx: AudioContext): AudioBuffer {
  let buf = noiseCache.get(ctx);
  if (!buf) {
    buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noiseCache.set(ctx, buf);
  }
  return buf;
}

/** Register a source so stop() can kill it, and forget it once it has ended on its own. */
function track<T extends AudioScheduledSourceNode>(v: Voice, node: T): T {
  v.sources.add(node);
  node.addEventListener('ended', () => {
    v.sources.delete(node);
    node.disconnect();
    if (!v.loop && v.sources.size === 0) v.onDrained();
  });
  return node;
}

function osc(v: Voice, type: OscillatorType, freq: number, dest: AudioNode | AudioParam): OscillatorNode {
  const node = v.ctx.createOscillator();
  node.type = type;
  node.frequency.value = freq;
  if (dest instanceof AudioParam) node.connect(dest);
  else node.connect(dest);
  return track(v, node);
}

function noise(v: Voice, dest: AudioNode): AudioBufferSourceNode {
  const node = v.ctx.createBufferSource();
  node.buffer = noiseBuffer(v.ctx);
  node.loop = true;
  node.connect(dest);
  return track(v, node);
}

/** Modulate an AudioParam with an LFO of the given depth. */
function lfo(v: Voice, param: AudioParam, hz: number, depth: number, at: number): OscillatorNode {
  const depthGain = v.ctx.createGain();
  depthGain.gain.value = depth;
  depthGain.connect(param);
  const node = osc(v, 'sine', hz, depthGain);
  node.start(at);
  return node;
}

interface EnvelopeSpec { peak: number; attack: number; decay: number; sustain: number; hold: number; release: number }

/** Linear attack, exponential decay to `sustain`, hold, then release. Returns the end time. */
function envelope(gain: GainNode, t0: number, e: EnvelopeSpec): number {
  const g = gain.gain;
  const sustain = Math.max(e.peak * e.sustain, 0.0001);
  g.setValueAtTime(0.0001, t0);
  g.linearRampToValueAtTime(e.peak, t0 + e.attack);
  const sustainAt = t0 + e.attack + e.decay;
  g.exponentialRampToValueAtTime(sustain, sustainAt);
  const releaseAt = sustainAt + e.hold;
  g.setValueAtTime(sustain, releaseAt);
  g.exponentialRampToValueAtTime(0.0001, releaseAt + e.release);
  return releaseAt + e.release;
}

interface ToneSpec extends Partial<EnvelopeSpec> { type?: OscillatorType; freq: number; endFreq?: number; at: number }

/** One enveloped tone (optional pitch glide); returns the end time. */
function tone(v: Voice, o: ToneSpec): number {
  const g = v.ctx.createGain();
  g.connect(v.out);
  const t0 = v.ctx.currentTime + o.at;
  const end = envelope(g, t0, {
    peak: o.peak ?? 0.5, attack: o.attack ?? 0.005, decay: o.decay ?? 0.1,
    sustain: o.sustain ?? 0.3, hold: o.hold ?? 0.05, release: o.release ?? 0.2,
  });
  const node = osc(v, o.type ?? 'sine', o.freq, g);
  node.frequency.setValueAtTime(o.freq, t0);
  if (o.endFreq !== undefined) node.frequency.exponentialRampToValueAtTime(o.endFreq, end);
  node.start(t0);
  node.stop(end + 0.05);
  return end;
}

interface BurstSpec extends Partial<EnvelopeSpec> { at: number; filter: BiquadFilterType; freq: number; endFreq?: number; q?: number }

/** Enveloped filtered noise (optional filter sweep); returns the end time. */
function burst(v: Voice, o: BurstSpec): number {
  const filter = v.ctx.createBiquadFilter();
  filter.type = o.filter;
  filter.Q.value = o.q ?? 1;
  const g = v.ctx.createGain();
  filter.connect(g);
  g.connect(v.out);
  const t0 = v.ctx.currentTime + o.at;
  filter.frequency.setValueAtTime(o.freq, t0);
  const end = envelope(g, t0, {
    peak: o.peak ?? 0.4, attack: o.attack ?? 0.005, decay: o.decay ?? 0.05,
    sustain: o.sustain ?? 0.2, hold: o.hold ?? 0, release: o.release ?? 0.08,
  });
  if (o.endFreq !== undefined) filter.frequency.exponentialRampToValueAtTime(o.endFreq, end);
  const n = noise(v, filter);
  n.start(t0);
  n.stop(end + 0.05);
  return end;
}

/** Fade the voice's master gain in from silence (used by loops so they never click). */
function fadeIn(v: Voice, seconds: number): void {
  const t0 = v.ctx.currentTime;
  v.out.gain.setValueAtTime(0.0001, t0);
  v.out.gain.linearRampToValueAtTime(v.volume, t0 + seconds);
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

const MAJOR = [523.25, 659.25, 783.99, 1046.5, 1318.5, 1567.98]; // C5 E5 G5 C6 E6 G6

const PRESETS: Record<Exclude<SynthPreset, 'none'>, (v: Voice) => void> = {
  click(v) {
    burst(v, { at: 0, filter: 'bandpass', freq: 2200, q: 2, decay: 0.02, release: 0.03, peak: 0.6 });
  },
  tick(v) {
    tone(v, { freq: 900, endFreq: 300, at: 0, decay: 0.06, sustain: 0.1, hold: 0, release: 0.06, peak: 0.6 });
  },
  whoosh(v) {
    burst(v, { at: 0, filter: 'bandpass', freq: 300, endFreq: 3000, q: 0.8, attack: 0.08, decay: 0.2, release: 0.25, peak: 0.5 });
  },
  hum(v) {
    const t0 = v.ctx.currentTime;
    const filter = v.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 400;
    filter.connect(v.out);
    osc(v, 'sawtooth', 55, filter).start(t0);
    osc(v, 'sine', 110, filter).start(t0);
    lfo(v, filter.frequency, 0.8, 150, t0);
    fadeIn(v, 0.15);
  },
  riser(v) {
    const g = v.ctx.createGain();
    g.gain.value = 0.35;
    g.connect(v.out);
    const t0 = v.ctx.currentTime;
    const voices = [osc(v, 'sawtooth', 110, g), osc(v, 'square', 112, g)];
    const hp = v.ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.connect(g);
    const n = noise(v, hp);
    const tremolo = lfo(v, g.gain, 6, 0.12, t0);
    for (const o of voices) o.start(t0);
    n.start(t0);
    /** One 4-second climb; loops so the tension keeps building for as long as anticipation lasts. */
    const climb = () => {
      const now = v.ctx.currentTime;
      voices.forEach((o, i) => {
        o.frequency.cancelScheduledValues(now);
        o.frequency.setValueAtTime(110 + i * 2, now);
        o.frequency.exponentialRampToValueAtTime((110 + i * 2) * 4, now + 4);
      });
      hp.frequency.cancelScheduledValues(now);
      hp.frequency.setValueAtTime(400, now);
      hp.frequency.exponentialRampToValueAtTime(4000, now + 4);
      tremolo.frequency.cancelScheduledValues(now);
      tremolo.frequency.setValueAtTime(6, now);
      tremolo.frequency.linearRampToValueAtTime(14, now + 4);
      v.timers.add(setTimeout(climb, 4000));
    };
    climb();
    fadeIn(v, 0.1);
  },
  chime(v) {
    tone(v, { freq: 1318.5, at: 0, decay: 0.2, sustain: 0.3, hold: 0.05, release: 0.4, peak: 0.45 });
    tone(v, { freq: 1975.5, at: 0.09, decay: 0.25, sustain: 0.3, hold: 0.05, release: 0.6, peak: 0.4 });
    tone(v, { type: 'triangle', freq: 2637, at: 0.09, decay: 0.15, sustain: 0.1, hold: 0, release: 0.3, peak: 0.15 });
  },
  coin(v) {
    const ping = (at: number, i: number) => {
      const base = 1800 + (i % 3) * 220;
      tone(v, { freq: base, endFreq: base * 1.02, at, decay: 0.03, sustain: 0.35, hold: 0.02, release: 0.09, peak: 0.35 });
      tone(v, { type: 'triangle', freq: base * 2.02, at, decay: 0.03, sustain: 0.2, hold: 0, release: 0.06, peak: 0.12 });
    };
    if (!v.loop) {
      [0, 1, 2].forEach((i) => ping(i * 0.09, i));
      return;
    }
    let i = 0;
    const tick = () => {
      ping(0, i++);
      v.timers.add(setTimeout(tick, 90));
    };
    tick();
  },
  fanfare(v) {
    [0, 1, 2].forEach((i) => {
      tone(v, { type: 'triangle', freq: MAJOR[i], at: i * 0.12, decay: 0.08, sustain: 0.6, hold: 0.1, release: 0.25, peak: 0.45 });
      tone(v, { type: 'sine', freq: MAJOR[i] / 2, at: i * 0.12, decay: 0.08, sustain: 0.5, hold: 0.1, release: 0.25, peak: 0.2 });
    });
    tone(v, { type: 'triangle', freq: MAJOR[3], at: 0.36, decay: 0.1, sustain: 0.6, hold: 0.35, release: 0.5, peak: 0.5 });
  },
  fanfareBig(v) {
    MAJOR.forEach((f, i) => {
      tone(v, { type: 'triangle', freq: f, at: i * 0.1, decay: 0.06, sustain: 0.5, hold: 0.06, release: 0.2, peak: 0.4 });
    });
    const chordAt = MAJOR.length * 0.1 + 0.05;
    for (const f of [523.25, 659.25, 783.99, 1046.5]) {
      tone(v, { type: 'sawtooth', freq: f, at: chordAt, attack: 0.02, decay: 0.15, sustain: 0.5, hold: 0.7, release: 0.9, peak: 0.16 });
      tone(v, { type: 'sine', freq: f, at: chordAt, attack: 0.02, decay: 0.15, sustain: 0.6, hold: 0.7, release: 0.9, peak: 0.22 });
    }
    burst(v, { at: chordAt, filter: 'highpass', freq: 3000, attack: 0.01, decay: 0.3, release: 0.6, peak: 0.12 });
  },
  sting(v) {
    tone(v, { type: 'triangle', freq: 1046.5, at: 0, attack: 0.003, decay: 0.12, sustain: 0.4, hold: 0.08, release: 0.35, peak: 0.6 });
    tone(v, { type: 'sine', freq: 523.25, at: 0, attack: 0.003, decay: 0.12, sustain: 0.4, hold: 0.08, release: 0.35, peak: 0.3 });
  },
  buzz(v) {
    tone(v, { type: 'square', freq: 160, endFreq: 120, at: 0, decay: 0.05, sustain: 0.7, hold: 0.12, release: 0.06, peak: 0.25 });
    tone(v, { type: 'square', freq: 163, endFreq: 121, at: 0, decay: 0.05, sustain: 0.7, hold: 0.12, release: 0.06, peak: 0.2 });
  },
  ambient(v) {
    const t0 = v.ctx.currentTime;
    const filter = v.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    filter.connect(v.out);
    lfo(v, filter.frequency, 0.07, 400, t0);
    // Soft add9 pad (C3 G3 E4 D5), each note as a detuned pair for width.
    for (const f of [130.81, 196, 329.63, 587.33]) {
      for (const detune of [-5, 5]) {
        const o = osc(v, 'triangle', f, filter);
        o.detune.value = detune;
        o.start(t0);
      }
    }
    fadeIn(v, 1.5);
  },
};

// ---------------------------------------------------------------------------
// Public entry
// ---------------------------------------------------------------------------

/**
 * Play a preset into `dest`. Loop presets (hum, riser, ambient, coin) run until `stop()`;
 * one-shots end on their own but are still cleaned up by `stop()`.
 * `stop()` ramps the voice to silence over 50 ms and disconnects everything.
 */
export function playSynth(
  ctx: AudioContext,
  dest: AudioNode,
  preset: SynthPreset,
  o: { volume: number; loop: boolean },
): SynthHandle {
  if (preset === 'none' || !PRESETS[preset]) return { stop() {} };

  const out = ctx.createGain();
  const volume = Math.max(0, o.volume);
  out.gain.value = volume;
  out.connect(dest);

  let stopped = false;
  const dispose = () => {
    for (const s of v.sources) {
      try { s.stop(); } catch { /* not started or already stopped */ }
      s.disconnect();
    }
    v.sources.clear();
    out.disconnect();
  };
  const v: Voice = {
    ctx, out, volume, loop: o.loop,
    sources: new Set(), timers: new Set(),
    onDrained: () => { if (!stopped) { stopped = true; dispose(); } },
  };

  const handle: SynthHandle = {
    stop() {
      if (stopped) return;
      stopped = true;
      for (const t of v.timers) clearTimeout(t);
      v.timers.clear();
      const now = ctx.currentTime;
      out.gain.cancelScheduledValues(now);
      out.gain.setValueAtTime(out.gain.value, now);
      out.gain.linearRampToValueAtTime(0, now + 0.05);
      setTimeout(dispose, 80);
    },
  };

  try {
    PRESETS[preset](v);
  } catch {
    handle.stop();
  }
  return handle;
}
